import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { IntegrationsApi, type IResponseGetHubspotActivityTypes } from 'entities/Integrations'

export class CallPopUpCallInsightCardsCallTypeStore {
  constructor() {
    makeAutoObservable(this)
    this.loadCallTypes()
  }

  private _search = ''
  private _loading = false
  private _showModal = false
  private _pendingOpen = false
  private _callTypes: Array<string> | null = null
  private _callBack: (() => void) | null = null
  private _callSid: string | null = null
  private _completedThisRound = false

  setCallBack = ({ callBack }: { callBack: () => void }) => {
    this._callBack = callBack
  }

  setCallSid = (callSid: string) => {
    if (callSid && callSid !== this._callSid) {
      this._completedThisRound = false
    }
    this._callSid = callSid
  }

  setSearch = (search: string) => {
    this._search = search
  }

  onSelectedCallType = async (type: string) => {
    if (this._callSid === null) return

    try {
      runInAction(() => {
        this._loading = true
      })

      await IntegrationsApi.saveHubspotCallTypeByCallSid({
        call_sid: this._callSid,
        type: type,
      }).then(() => {
        toastStore.add({
          title: 'Call type saved',
          type: 'success',
        })
      })
    } catch (e) {
      if (e instanceof Error) {
        toastStore.add({
          title: e.message,
          type: 'error',
        })
      }
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
      this.closeCallType()
    }
  }

  setCallTypes = (types: IResponseGetHubspotActivityTypes) => {
    this._callTypes = types
    if (!this._pendingOpen) return
    this._pendingOpen = false
    if ((types?.length ?? 0) > 0) {
      this._showModal = true
    } else if (this._callBack) {
      this._completedThisRound = true
      this._callBack()
    }
  }

  private _resolvePendingWithoutModal = () => {
    const hadPending = this._pendingOpen
    this._pendingOpen = false
    this._showModal = false
    if (hadPending && this._callBack) {
      this._completedThisRound = true
      this._callBack()
    }
  }

  loadCallTypes = async () => {
    try {
      runInAction(() => {
        this._loading = true
      })

      const { data } = await IntegrationsApi.getHubspotActivityTypes()
      if (data) {
        this.setCallTypes(data)
      } else {
        runInAction(() => {
          this._resolvePendingWithoutModal()
        })
      }
    } catch (e) {
      runInAction(() => {
        this._resolvePendingWithoutModal()
      })
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  setModalStatus = (status: boolean) => {
    if (status && this._completedThisRound) {
      return
    }
    if (status && !this._callTypes?.length) {
      if (this._loading) {
        this._pendingOpen = true
      }
      return
    }
    const wasActive = this._showModal || this._pendingOpen
    this._pendingOpen = false
    this._showModal = status
    if (!status && wasActive && this._callBack) {
      this._completedThisRound = true
      this._callBack()
    }
  }

  handleSkipCallType = () => {
    this.closeCallType()
  }

  closeCallType = () => {
    this._pendingOpen = false
    this._showModal = false
    this._completedThisRound = true
    this._callBack?.()
  }

  get search() {
    return this._search
  }

  get filteredCallTypes() {
    if (this._search) {
      return this._callTypes?.filter((type) =>
        type?.toLowerCase().includes(this._search.toLowerCase())
      )
    }

    return this._callTypes
  }

  get loading() {
    return this._loading
  }

  get showModal() {
    return this._showModal || this._pendingOpen
  }

  get hasCallTypes() {
    return (this._callTypes?.length ?? 0) > 0
  }
}
